/**
 * park-* design-system — bundle entry / barrel (epic #138).
 *
 * RESET 2026-06-26 (see ../../../docs/design-system-rebuild.md): the original
 * component set was removed because it mirrored the static design-system/*.html
 * mockups rather than the live pages, so #150 migration would have regressed the
 * site. The infrastructure below (light-DOM contract in base.ts, the esbuild
 * IIFE bundle, window.ParkDS) is unchanged and still correct — only the component
 * implementations were removed, to be rebuilt against the LIVE design.
 *
 * This file is the single esbuild entry point (#140). Bundling it as an IIFE:
 *   1. runs every element module's top-level `defineElement(...)` as a side
 *      effect, registering all `park-*` custom elements, and
 *   2. publishes `window.ParkDS = { version, elements }`.
 *
 * Adding a component (rebuild phase): create `park-<name>.ts` by extracting the
 * markup/classes from the shipping page it represents, then add its side-effect
 * import below. Nothing else.
 */

import type { ParkDSGlobal } from "./base";
import { registeredTags } from "./base";

/* ── Registered elements ──────────────────────────────────────────────────
 * Each import is a SIDE EFFECT: importing the module calls defineElement().
 * Rebuild in progress — see docs/design-system-rebuild-plan.md.
 */

import "./park-button"; // #141 — isomorphic reference implementation
import "./park-page-header"; // #187 — shared page title + intro block
import "./park-maintenance-banner"; // #148 — public maintenance notice
import "./park-hero"; // #143 — landing hero (two-column + media slot)
import "./park-steps"; // #144 — <park-steps> + <park-step> process grid
import "./park-section-card"; // #142 — island/price card
import "./park-cta"; // #188 — closing call-to-action band
import "./park-gallery"; // #149 — <park-gallery-grid> + <park-gallery-card>
import "./park-field"; // #147 — <park-field> + <park-notice> form primitives
import "./park-booking-calendar"; // #146 — free/busy calendar shell (JS-coupled)
import "./park-stage"; // #1387 — staged-flow stage shell (JS-coupled)
import "./park-choice"; // fix-stage-island-ux — radio-button choice group
import "./park-rule-group"; // #1402 — information-board group card
import "./park-rule"; // #1402 — information-board guidance item
import "./park-board-heading"; // #1405 — information-board pill heading (standalone)

/* Operator-facing (admin) elements — design-system#FR-3 covers operator surfaces
 * as of anton/forrest#999; these are the first of them (anton/forrest#1000). */
import "./park-admin-page-header"; // #1000 — admin page title block
import "./park-admin-section"; // #1000 — titled admin section block
import "./park-admin-tile"; // #1000 — <park-admin-tile-grid> + <park-admin-tile>
import "./park-admin-row"; // #1000 — <park-admin-list> + <park-admin-row>
import "./park-admin-status"; // #1021 — action-result banner (GalleryManager)
import "./park-admin-empty"; // #1021 — directive empty state (GalleryManager)
import "./park-admin-caption"; // #1248 — labelled caption/alt-text editor (GalleryManager)
import "./park-admin-actions"; // #1248 — row/form action-button cluster (GalleryManager)

/** Library version. Bump on contract-affecting changes. */
const VERSION = "0.2.0";

declare global {
	interface Window {
		ParkDS?: ParkDSGlobal;
	}
}

// Publish the registry. Runs after the imports above, so `registeredTags` is
// fully populated. Guarded for non-DOM contexts (the bundle is browser-only,
// but this keeps the entry import-safe).
if (typeof window !== "undefined") {
	window.ParkDS = { version: VERSION, elements: [...registeredTags] };
}

export type { ParkDSGlobal };
